import AiScribeAudioControls from '@/components/AiScribeAudioControls';
import AiScribeInsights from '@/components/AiScribeInsights';
import AiScribeTranscript from '@/components/AiScribeTranscript';
import AppointmentList from '@/components/AppointmentList';
import { ScribeJob } from '@/components/EncounterHistory';
import Layout from '@/components/Layout';
import PatientInfoBar from '@/components/PatientInfoBar';
import PatientInfoSidebar from '@/components/PatientInfoSidebar';
import { Button } from '@/components/ui/button';
import GlassCard from '@/components/ui/glass-card';
import NewEncounterDialog from '@/components/ui/new-encounter-dialog';
import {
  RecordingProvider,
  useRecordingContext,
} from '@/contexts/RecordingContext';
import { useAiScribeLiveTranscript } from '@/hooks/use-ai-scribe-live-transcript';

import { useAuthRedux } from '@/hooks/use-auth-redux';
import { useSectionRecordingSubscription } from '@/hooks/use-section-recording-subscription';
import {
  AiScribeCompleteResponse,
  AiScribeGetJobDetailsResponse,
} from '@/lib/ai-scribe-service';
import { pageTransition } from '@/lib/animations';
import { getAudioUrl } from '@/lib/audio-utils';
import { gatewayService } from '@/lib/gateway-service';
import { supabase } from '@/lib/supabase';
import {
  fetchAndProcessTranscriptWithTiming,
  type ProcessedTranscript,
} from '@/lib/transcript-utils';
import { generateAlphanumericId } from '@/lib/utils';
import {
  AppointmentsStateSelector,
  RecordingStateSelector,
  SelectedLocationStateSelector,
} from '@/store/selectors';
import {
  clearSelectedAppointment,
  setSelectedAppointment,
  updateAppointmentScribeJobId,
} from '@/store/slices/dataSlice';
import { resetRecording } from '@/store/slices/recordingSlice';
import { type SimplifiedAppointment } from '@/types/appointment';
import { motion } from 'framer-motion';
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Mic,
  Plus,
  Sparkles,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Types for appointment data state
interface AppointmentDataState {
  jobDetails: AiScribeGetJobDetailsResponse | null;
  transcriptContent: string | null;
  processedTranscript: ProcessedTranscript | null;
  isLoading: boolean;
  error: string | null;
  lastFetchedAppointmentId: string | null;
}

// ADD a blank initial state for our new live transcript accumulator
const initialProcessedTranscript: ProcessedTranscript = {
  formattedText: '',
  clickableLines: [],
  isJson: false,
};

// Inner component that has access to RecordingContext
const AiScribeContent = () => {
  const dispatch = useDispatch();
  const { user } = useAuthRedux();
  const { forceCleanup } = useRecordingContext();
  const selectedAppointment = useSelector(
    AppointmentsStateSelector.selectedAppointment
  );
  const isRecording = useSelector(RecordingStateSelector.isRecording);
  const processingStarted = useSelector(
    RecordingStateSelector.processingStarted
  );

  // Initialize section recording subscription
  // This will start when ANY section recording begins and persist until appointment changes
  const { isConnected: isSubscriptionConnected, cleanup: cleanupSubscription } =
    useSectionRecordingSubscription(true);

  useEffect(() => {
    if (isSubscriptionConnected) {
      console.log('✅ Section recording subscription is active');
    }
  }, [isSubscriptionConnected]);

  // Detect current screen size to determine which audio controls should be active
  const [currentBreakpoint, setCurrentBreakpoint] = useState<
    'mobile' | 'tablet' | 'desktop'
  >('mobile');

  useEffect(() => {
    const updateBreakpoint = () => {
      const width = window.innerWidth;
      const newBreakpoint =
        width < 768 ? 'mobile' : width < 1536 ? 'tablet' : 'desktop';

      // Clear current audio element when breakpoint changes to prevent conflicts
      if (newBreakpoint !== currentBreakpoint) {
        setCurrentAudioElement(null);
      }

      setCurrentBreakpoint(newBreakpoint);
    };

    updateBreakpoint();
    window.addEventListener('resize', updateBreakpoint);
    return () => window.removeEventListener('resize', updateBreakpoint);
  }, [currentBreakpoint]);

  // State for transcript panel visibility
  const [isTranscriptVisible, setIsTranscriptVisible] = useState(false);

  // State to track if transcript should animate when showing/hiding
  const [shouldAnimateTranscript, setShouldAnimateTranscript] = useState(true);

  const [isTranscriptLoading, setIsTranscriptLoading] = useState(false);

  // State for audio playback (to sync with transcript clicks)
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);

  // State for highlighting segments
  const [highlightedSegmentIds, setHighlightedSegmentIds] = useState<string[]>(
    []
  );

  const [selectedSection, setSelectedSection] = useState<string>('');

  const [liveProcessedTranscript, setLiveProcessedTranscript] =
    useState<ProcessedTranscript>(initialProcessedTranscript);

  // Track current audio element to avoid DOM queries
  const [currentAudioElement, setCurrentAudioElement] =
    useState<HTMLAudioElement | null>(null);

  // Audio player state for shared audio element
  const [audioUrl, setAudioUrl] = useState<string>('');
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);

  // Get selected location
  const selectedLocationId = useSelector(
    SelectedLocationStateSelector.locationId
  );

  // State for new encounter dialog
  const [isNewEncounterDialogOpen, setIsNewEncounterDialogOpen] =
    useState(false);
  const [isCreatingEncounter, setIsCreatingEncounter] = useState(false);

  // State for controlling appointment list tab
  const [appointmentListActiveTab, setAppointmentListActiveTab] =
    useState('today');
  const [pastEncountersRefreshTrigger, setPastEncountersRefreshTrigger] =
    useState(0);

  // State for hover sidebar
  const [isSidebarHovered, setIsSidebarHovered] = useState(false);

  // Consolidated appointment data state
  const [appointmentData, setAppointmentData] = useState<AppointmentDataState>({
    jobDetails: null,
    transcriptContent: null,
    processedTranscript: null,
    isLoading: false,
    error: null,
    lastFetchedAppointmentId: null,
  });

  const [selectedEncounter, setSelectedEncounter] = useState<ScribeJob | null>(
    null
  );

  // State for controlling status view (new layout for non-completed jobs)
  const [isStatusViewActive, setIsStatusViewActive] = useState(false);

  // Real-time subscription job ID (ONLY from createAiScribeJob response via Redux)
  const realtimeJobId = selectedAppointment?.scribeJobId; // Only use actual scribeJobId from createAiScribeJob

  const handleLiveTranscriptUpdate = useCallback(
    (newPart: ProcessedTranscript) => {
      console.log('🔄 onTranscriptUpdate called with newPart:', newPart);
      setLiveProcessedTranscript((prev) => {
        // Make sure we don't add duplicate lines if an update is ever re-sent
        const existingLineTexts = new Set(
          prev.clickableLines.map((line) => line.text)
        );
        const uniqueNewLines = newPart.clickableLines.filter(
          (line) => !existingLineTexts.has(line.text)
        );

        if (uniqueNewLines.length === 0) {
          return prev; // No change needed
        }

        return {
          formattedText:
            prev.formattedText +
            '\n' +
            uniqueNewLines.map((l) => l.text).join('\n'),
          clickableLines: [...prev.clickableLines, ...uniqueNewLines],
          isJson: newPart.isJson,
        };
      });
    },
    []
  );

  // This is now the ONLY place this hook is called.
  // It provides all the necessary state for the live transcript.
  const {
    transcriptContent: liveTranscriptFromHook,
    isTyping: isLiveTranscriptTyping,
    isConnected: isLiveTranscriptConnected,
    error: liveTranscriptError,
    clearTranscript,
  } = useAiScribeLiveTranscript({
    scribeJobId: realtimeJobId,
    enabled: isRecording && !!realtimeJobId,
    onTranscriptUpdate: handleLiveTranscriptUpdate,
  });

  // Ref to track current appointment being processed
  const currentAppointmentRef = useRef<string | null>(null);

  // Live transcript pipeline (realtime → CF → fetch → typing)
  const [pauseTyping] = useState<() => void>(() => () => {});
  const [resumeTyping] = useState<() => void>(() => () => {});

  // Single function to fetch job details for an appointment
  const fetchJobDetailsForAppointment = useCallback(
    async (appointment: SimplifiedAppointment) => {
      if (!appointment) return;

      const appointmentId = appointment.appointmentId;
      const locationId = selectedLocationId || appointment.locationId;

      if (!locationId) {
        setAppointmentData((prev) => ({
          ...prev,
          error: 'Location ID is required',
          isLoading: false,
        }));
        return;
      }

      // Check if we're already loading this appointment
      if (
        currentAppointmentRef.current === appointmentId &&
        appointmentData.isLoading
      ) {
        console.log('🔄 Already loading appointment:', appointmentId);
        return;
      }

      // Update current appointment ref
      currentAppointmentRef.current = appointmentId;

      // Clear previous data and set loading state
      setAppointmentData({
        jobDetails: null,
        transcriptContent: null,
        processedTranscript: null,
        isLoading: true,
        error: null,
        lastFetchedAppointmentId: appointmentId,
      });

      setHighlightedSegmentIds([]);
      setSelectedSection('');

      try {
        let jobData: any = null;

        // Scenario 1: Appointment has scribeJobId - fetch directly by job ID
        if (appointment.scribeJobId) {
          console.log(
            '🎯 Fetching job details by scribeJobId:',
            appointment.scribeJobId
          );

          const result = await gatewayService.getJobDetailsByJobId(
            appointment.scribeJobId
          );

          if (result.success && result.data) {
            jobData = result.data;

            // Store parts in Redux with the selected appointment
            if (jobData?.job?.parts) {
              dispatch(
                setSelectedAppointment({
                  ...appointment,
                  parts: jobData.job.parts,
                })
              );
              console.log(
                `✅ Stored ${jobData.job.parts.length} parts in Redux for appointment`
              );
            }
          } else {
            // Check if it's a "not found" vs actual error
            const isNotFoundError =
              result.error?.includes('not found') ||
              result.error?.includes('Not found') ||
              result.statusCode === 404;

            if (isNotFoundError) {
              console.log(
                'ℹ️ No existing job found for scribeJobId, ready for new recording'
              );
              setAppointmentData({
                jobDetails: null,
                transcriptContent: null,
                processedTranscript: null,
                isLoading: false,
                error: null,
                lastFetchedAppointmentId: appointmentId,
              });
              return; // No job found is not an error - allow recording
            } else {
              throw new Error(
                result.error || 'Failed to fetch job details by scribeJobId'
              );
            }
          }
        }
        // Scenario 2: Appointment doesn't have scribeJobId - fetch by appointment info
        else {
          console.log('🔍 Fetching job details by appointment info:', {
            appointmentId: appointment.appointmentId,
            patientId: appointment.patientId,
            locationId: locationId,
          });

          const result = await gatewayService.getJobDetailsByAppointmentInfo(
            appointment.appointmentId,
            locationId,
            appointment.patientId
          );

          if (result.success && result.data) {
            jobData = result.data;

            // If we found a job, update the appointment with the scribeJobId for future reference
            if (jobData?.job?.id) {
              dispatch(
                updateAppointmentScribeJobId({
                  appointmentId: appointment.appointmentId,
                  scribeJobId: jobData.job.id,
                })
              );
            }

            // Store parts in Redux with the selected appointment
            if (jobData?.job?.parts) {
              dispatch(
                setSelectedAppointment({
                  ...appointment,
                  scribeJobId: jobData.job.id,
                  parts: jobData.job.parts,
                })
              );
              console.log(
                `✅ Stored ${jobData.job.parts.length} parts in Redux for appointment`
              );
            }
          } else {
            // Check if it's a "not found" vs actual error
            const isNotFoundError =
              result.error?.includes('not found') ||
              result.error?.includes('Not found') ||
              result.statusCode === 404;

            if (isNotFoundError) {
              console.log(
                'ℹ️ No existing job found for appointment, ready for new recording'
              );
              setAppointmentData({
                jobDetails: null,
                transcriptContent: null,
                processedTranscript: null,
                isLoading: false,
                error: null,
                lastFetchedAppointmentId: appointmentId,
              });
              return; // No job found is not an error
            } else {
              throw new Error(
                result.error ||
                  'Failed to fetch job details by appointment info'
              );
            }
          }
        }

        // Set job details if we found them
        if (jobData) {
          console.log('✅ Job details fetched successfully:', {
            jobId: jobData.job?.id,
            status: jobData.job?.status,
            hasTranscriptUrl: !!jobData.job?.transcriptFileUrl,
            hasAudioUrl: !!jobData.job?.audioFileUrl,
          });

          setAppointmentData((prev) => ({
            ...prev,
            jobDetails: jobData,
            isLoading: false,
            error: null,
          }));
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error occurred';
        console.error('❌ Error fetching job details:', errorMessage);

        setAppointmentData((prev) => ({
          ...prev,
          error: errorMessage,
          isLoading: false,
        }));
      }
    },
    [selectedLocationId, dispatch]
  );

  // Fetch transcript content from URL (supports both JSON and text formats)
  const fetchTranscriptFromUrl = useCallback(
    async (
      transcriptFileUrl: string,
      appointmentId: string,
      forceFetch = false
    ) => {
      if (!transcriptFileUrl.trim()) return;

      // Only fetch if this is still the current appointment
      if (currentAppointmentRef.current !== appointmentId && !forceFetch) {
        console.log('🔄 Skipping transcript fetch - appointment changed');
        return;
      }

      try {
        console.log('📥 Fetching transcript from URL:', transcriptFileUrl);
        console.log(
          '🔍 Current appointment ref:',
          currentAppointmentRef.current
        );
        console.log('🔍 Target appointment ID:', appointmentId);

        // Fetch and process the transcript using shared utility (auto-detects format)
        console.log('🔄 Calling fetchAndProcessTranscriptWithTiming...');
        setIsTranscriptLoading(true);
        const processedTranscript = await fetchAndProcessTranscriptWithTiming(
          transcriptFileUrl
        );
        console.log(
          '✅ fetchAndProcessTranscriptWithTiming completed successfully'
        );
        console.log('📝 Processed transcript returned:', {
          formattedLength: processedTranscript.formattedText.length,
          clickableLinesCount: processedTranscript.clickableLines.length,
          isJson: processedTranscript.isJson,
          preview: processedTranscript.formattedText.substring(0, 100) + '...',
        });
        setIsTranscriptLoading(false);

        // Only update if this is still the current appointment
        if (forceFetch || currentAppointmentRef.current === appointmentId) {
          console.log('📝 PROCESSED TRANSCRIPT CONTENT:', {
            formattedText: processedTranscript.formattedText,
            length: processedTranscript.formattedText.length,
            clickableLinesCount: processedTranscript.clickableLines.length,
            preview:
              processedTranscript.formattedText.substring(0, 200) + '...',
          });

          setAppointmentData((prev) => ({
            ...prev,
            transcriptContent: processedTranscript.formattedText,
            processedTranscript: processedTranscript,
          }));
          console.log('✅ Transcript content updated successfully');
        }
      } catch (error) {
        console.error('Error fetching transcript:', error);
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to fetch transcript';

        // Only update error if this is still the current appointment
        if (currentAppointmentRef.current === appointmentId) {
          setAppointmentData((prev) => ({
            ...prev,
            error: errorMessage,
          }));
        }
      }
    },
    []
  );

  // Handle appointment selection
  const handleAppointmentSelect = useCallback(
    async (appointment: SimplifiedAppointment) => {
      clearTranscript();
      setLiveProcessedTranscript(initialProcessedTranscript);
      // Clear current audio element when switching appointments
      setCurrentAudioElement(null);
      try {
        console.log(
          '📋 Appointment selection started:',
          appointment.appointmentId
        );

        // Clear current appointment ref to cancel any ongoing operations
        currentAppointmentRef.current = null;

        // Clear all data immediately
        setAppointmentData({
          jobDetails: null,
          transcriptContent: null,
          processedTranscript: null,
          isLoading: false,
          error: null,
          lastFetchedAppointmentId: null,
        });

        dispatch(resetRecording());
        setShouldAnimateTranscript(false); // Disable animation when showing via appointment selection
        setIsTranscriptVisible(true); // Show transcript immediately when appointment selected
        if (
          appointment?.status &&
          (appointment?.status === 'PENDING' ||
            appointment?.status === 'created')
        ) {
          setIsStatusViewActive(true);
        }
        // Validate required appointment data
        if (!appointment.appointmentId) {
          throw new Error('Appointment ID is missing');
        }

        if (!appointment.patientId) {
          throw new Error('Patient ID is required to check for existing jobs');
        }

        const locationId = selectedLocationId || appointment.locationId;
        if (!locationId) {
          throw new Error('Location ID is required to check for existing jobs');
        }

        console.log('✅ Appointment selection validation complete');

        // Fetch job details for the selected appointment
        await fetchJobDetailsForAppointment(appointment);
      } catch (error) {
        console.error('❌ Error in handleAppointmentSelect:', error);
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to process appointment selection';

        setAppointmentData((prev) => ({
          ...prev,
          error: errorMessage,
          isLoading: false,
        }));
      }
    },
    [selectedLocationId, fetchJobDetailsForAppointment, clearTranscript]
  );

  // The handler passed to AiScribeAudioControls
  const handleRecordingStopped = useCallback(
    async (result: AiScribeCompleteResponse) => {
      console.log('🔄 handleRecordingStopped called with result:', result);
      if (result?.transcriptUrl && selectedAppointment?.appointmentId) {
        await fetchTranscriptFromUrl(
          result?.transcriptUrl,
          selectedAppointment?.appointmentId,
          true
        );
      }
    },
    [fetchTranscriptFromUrl, selectedAppointment]
  );

  // Fetch transcript when job details are available
  useEffect(() => {
    const transcriptFileUrl =
      appointmentData.jobDetails?.job?.transcriptFileUrl;
    const appointmentId = appointmentData.lastFetchedAppointmentId;

    if (isTranscriptLoading) {
      console.log('🔄 Skipping transcript fetch - transcript is loading');
      return;
    }

    if (transcriptFileUrl && transcriptFileUrl.trim() && appointmentId) {
      fetchTranscriptFromUrl(transcriptFileUrl, appointmentId);
    } else {
      console.log('❌ Skipping transcript fetch - missing data:', {
        hasUrl: !!transcriptFileUrl,
        hasAppointmentId: !!appointmentId,
        urlTrimmed: transcriptFileUrl?.trim(),
      });
    }
  }, [
    appointmentData.jobDetails?.job?.transcriptFileUrl,
    appointmentData.lastFetchedAppointmentId,
    fetchTranscriptFromUrl,
  ]);

  // Clear data when appointment changes (safety check)
  useEffect(() => {
    if (
      selectedAppointment?.appointmentId !==
      appointmentData.lastFetchedAppointmentId
    ) {
      console.log('🔄 Appointment changed, clearing data');
      currentAppointmentRef.current = null;
      setAppointmentData({
        jobDetails: null,
        transcriptContent: null,
        processedTranscript: null,
        isLoading: false,
        error: null,
        lastFetchedAppointmentId: null,
      });
    }
  }, [
    selectedAppointment?.appointmentId,
    appointmentData.lastFetchedAppointmentId,
  ]);

  // Clear appointment data when navigating away from AI Scribe page
  useEffect(() => {
    // Return function that runs when the component unmounts
    return () => {
      console.log(
        '🧹 AI Scribe component unmounting, clearing appointment data'
      );

      // Cleanup section recording subscription
      cleanupSubscription();

      dispatch(clearSelectedAppointment());
      dispatch(resetRecording());
      currentAppointmentRef.current = null;
      setAppointmentData({
        jobDetails: null,
        transcriptContent: null,
        processedTranscript: null,
        isLoading: false,
        error: null,
        lastFetchedAppointmentId: null,
      });

      setLiveProcessedTranscript(initialProcessedTranscript);
      setIsTranscriptVisible(false);
      setShouldAnimateTranscript(true);
      setIsTranscriptLoading(false);
      setIsAudioPlaying(false);
      setHighlightedSegmentIds([]);
      setSelectedSection('');
      setCurrentAudioElement(null);
      setIsStatusViewActive(false);
      clearTranscript();
    };
  }, [dispatch, clearTranscript]);

  // Auto-activate status view for non-completed jobs or during recording
  useEffect(() => {
    const jobStatus = appointmentData.jobDetails?.job?.status?.toLowerCase();

    // Show status view when:
    // 1. Recording is active (even without a job yet)
    // 2. Job exists but is not completed
    if (isRecording) {
      setIsStatusViewActive(true);
    } else if (
      appointmentData.jobDetails &&
      jobStatus &&
      jobStatus !== 'completed'
    ) {
      setIsStatusViewActive(true);
    } else if (jobStatus === 'completed') {
      // Reset status view when job is completed and not recording
      setIsStatusViewActive(false);
    } else if (
      !appointmentData.jobDetails &&
      selectedAppointment?.appointmentId
    ) {
      setIsStatusViewActive(true);
    }
  }, [appointmentData.jobDetails, isRecording]);

  // Clear typing animation when recording stops
  useEffect(() => {
    if (!isRecording) {
      try {
        pauseTyping();
      } catch {}
    } else {
      try {
        resumeTyping();
      } catch {}
    }
  }, [isRecording, pauseTyping, resumeTyping]);

  // Auto-show transcript panel when recording starts
  useEffect(() => {
    if (isRecording) {
      setShouldAnimateTranscript(false);
      setIsTranscriptVisible(true);
    }
  }, [isRecording]);

  // Optimized auto-hide transcript logic with memoized dependencies
  useEffect(() => {
    const hasAudio =
      selectedAppointment?.audioUrl ||
      appointmentData.jobDetails?.job?.audioFileUrl ||
      appointmentData.jobDetails?.job?.audioFileKey ||
      getAudioUrl();

    const hasTranscript =
      appointmentData.jobDetails?.job?.transcriptFileUrl ||
      appointmentData.transcriptContent;

    if (
      isTranscriptVisible &&
      !hasAudio &&
      !hasTranscript &&
      !selectedAppointment
    ) {
      setShouldAnimateTranscript(true);
      setIsTranscriptVisible(false);
    }
  }, [
    // Reduced dependencies to prevent excessive re-renders
    selectedAppointment?.appointmentId, // Only track ID changes, not full object
    appointmentData.jobDetails?.job?.audioFileUrl,
    appointmentData.jobDetails?.job?.transcriptFileUrl,
    appointmentData.transcriptContent,
    isTranscriptVisible,
  ]);

  // Callback for toggling transcript visibility
  const handleToggleTranscriptVisibility = useCallback(() => {
    setShouldAnimateTranscript(true);
    setIsTranscriptVisible((prev) => !prev);
  }, []);

  // Handler for highlighting segments from insights
  const handleHighlightSegments = useCallback(
    (segmentIds: string[]) => {
      setHighlightedSegmentIds(segmentIds);
      if (segmentIds.length === 0) {
        setSelectedSection('');
      }
    },
    [highlightedSegmentIds, setSelectedSection]
  );

  // Handler for creating new encounter
  const handleCreateNewEncounter = useCallback(
    async (data: {
      firstName: string;
      lastName: string;
      gender: string;
      dateOfBirth: string;
      email: string;
      phone: string;
      service: string;
    }) => {
      if (!selectedLocationId || !user?.id) {
        throw new Error('Location or user not available');
      }

      setIsCreatingEncounter(true);

      try {
        // Generate alphanumeric IDs for appointment and patient
        const appointmentId = generateAlphanumericId(20);
        const patientId = generateAlphanumericId(18);
        const scribeJobId = crypto.randomUUID();

        // Format current time as appointment start time
        const appointmentStartTime = new Date().toISOString();

        // Create the scribe job record directly in Supabase
        const { data: insertData, error: insertError } = await supabase
          .from('ev_scribe_jobs')
          .insert({
            id: scribeJobId,
            appointment_id: appointmentId,
            location_id: selectedLocationId,
            patient_id: patientId,
            pt_first_name: data.firstName,
            pt_last_name: data.lastName,
            pt_dob: data.dateOfBirth,
            pt_gender: data.gender,
            pt_email: data.email,
            pt_phone_number: data.phone,
            service: data.service,
            appointment_start_time: appointmentStartTime,
            status: 'CREATED',
            created_by:
              user.first_name && user.last_name
                ? `${user.first_name} ${user.last_name}`
                : user.username || user.email || 'Unknown User',
            audio_file_key: '',
            transcript_file_key: '',
            eyecare_report_key: '',
          })
          .select()
          .single();

        if (insertError) {
          throw new Error(
            `Failed to create scribe job: ${insertError.message}`
          );
        }

        if (!insertData) {
          throw new Error('No data returned after creating scribe job');
        }

        const createdScribeJobId = insertData.id;

        // Switch to Past Encounters tab and refresh the list
        setAppointmentListActiveTab('past');

        const newEncounter: ScribeJob = {
          id: insertData.id,
          appointment_id: insertData.appointment_id,
          patient_id: insertData.patient_id,
          pt_first_name: insertData.pt_first_name,
          pt_last_name: insertData.pt_last_name,
          pt_dob: insertData.pt_dob,
          pt_gender: insertData.pt_gender,
          pt_email: insertData.pt_email,
          pt_phone_number: insertData.pt_phone_number,
          service: insertData.service,
          appointment_start_time: insertData.appointment_start_time,
          status: insertData.status,
          location_id: insertData.location_id,
          start_time: insertData.start_time,
          created_by: insertData.created_by,
          end_time: insertData.end_time,
        };
        // Close the dialog
        setIsNewEncounterDialogOpen(false);
        setPastEncountersRefreshTrigger((prev) => prev + 1);
        setSelectedEncounter(newEncounter);
      } catch (error) {
        console.error('Error creating new encounter:', error);
        throw error;
      } finally {
        setIsCreatingEncounter(false);
      }
    },
    [selectedLocationId, user, dispatch]
  );

  // Memoized derived values to prevent unnecessary re-renders
  const derivedValues = useMemo(
    () => ({
      aiScribeLoading: appointmentData.isLoading,
      aiScribeError: appointmentData.error,
      aiScribeJobCompleted:
        appointmentData.jobDetails?.job?.status === 'COMPLETED' || false,
      processingStatus: appointmentData.jobDetails?.job?.status || 'PENDING',
      estimatedCompletionTime: null, // Not available in current response format
    }),
    [
      appointmentData.isLoading,
      appointmentData.error,
      appointmentData.jobDetails?.job?.status,
    ]
  );

  const {
    aiScribeLoading,
    aiScribeError,
    aiScribeJobCompleted,
    processingStatus,
    estimatedCompletionTime,
  } = derivedValues;

  return (
    <Layout>
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/3 via-transparent via-30% to-blue-900/4" />

        <div className="absolute top-1/4 left-1/4 w-80 h-80 bg-gradient-to-r from-purple-500/5 to-blue-500/5 rounded-full blur-3xl opacity-60" />
        <div className="absolute bottom-1/3 right-1/3 w-64 h-64 bg-gradient-to-l from-blue-500/4 to-purple-500/4 rounded-full blur-3xl opacity-60" />
      </div>

      <motion.div
        className="w-full flex flex-col h-full relative z-10"
        {...pageTransition}>
        {/* Main Content Area with reduced spacing */}
        <div className="flex-1 relative custom-for-mobile">
          {/* Mobile/iPad Hover Sidebar Indicator */}
          <div
            className="2xl:hidden fixed left-0 top-1/2 -translate-y-1/2 z-50 w-6 h-40 bg-gradient-to-b from-purple-500/90 to-blue-800/90 rounded-r-2xl shadow-2xl cursor-pointer transition-all duration-300 ease-out hover:shadow-purple-500/25 hover:shadow-2xl group border border-white/20 backdrop-blur-sm"
            onMouseEnter={() => setIsSidebarHovered(true)}
            onClick={() => setIsSidebarHovered(!isSidebarHovered)}>
            {/* Inner glow effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-white/20 via-white/10 to-white/20 rounded-r-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

            {/* Dynamic Arrow indicator */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
              {isSidebarHovered ? (
                <ChevronLeft className="w-7 h-7 text-white/70 group-hover:text-white transition-all duration-300 group-hover:-translate-x-0.5" />
              ) : (
                <ChevronRight className="w-7 h-7 text-white/70 group-hover:text-white transition-all duration-300 group-hover:translate-x-0.5" />
              )}
            </div>

            {/* Animated arrow shadow for depth */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              {isSidebarHovered ? (
                <ChevronLeft className="w-7 h-7 text-white/30 blur-sm" />
              ) : (
                <ChevronRight className="w-7 h-7 text-white/30 blur-sm" />
              )}
            </div>
          </div>

          {/* Mobile/iPad Hover Sidebar */}
          <div
            className={`2xl:hidden fixed left-0 top-22 h-[calc(100vh-7rem)] z-40 transition-all duration-300 ease-in-out ${
              isSidebarHovered ? 'translate-x-0' : '-translate-x-full'
            }`}
            onMouseEnter={() => setIsSidebarHovered(true)}>
            <div className="w-96 h-full bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border-r border-slate-200/60 dark:border-slate-600/40 shadow-2xl">
              <div className="h-full overflow-hidden p-4">
                <AppointmentList
                  onAppointmentSelect={(appointment) => {
                    handleAppointmentSelect(appointment);
                    setIsSidebarHovered(false);
                  }}
                  activeTab={appointmentListActiveTab}
                  onTabChange={setAppointmentListActiveTab}
                  refreshTrigger={pastEncountersRefreshTrigger}
                  selectedEncounter={selectedEncounter}
                />
              </div>
            </div>
          </div>

          {/* Backdrop - closes sidebar when clicked */}
          {isSidebarHovered && (
            <div
              className="2xl:hidden fixed inset-0 bg-black/20 z-30"
              onClick={() => setIsSidebarHovered(false)}
            />
          )}

          <div className="flex h-full relative gap-4">
            {/* Desktop Appointment List Sidebar - Always Visible (hidden in status view) */}
            {!isStatusViewActive && (
              <div className="hidden 2xl:block w-1/5 flex-shrink-0 custom-for-desktop">
                <GlassCard className="h-full" variant="elevated">
                  <AppointmentList
                    onAppointmentSelect={handleAppointmentSelect}
                    activeTab={appointmentListActiveTab}
                    onTabChange={setAppointmentListActiveTab}
                    refreshTrigger={pastEncountersRefreshTrigger}
                    selectedEncounter={selectedEncounter}
                  />
                </GlassCard>
              </div>
            )}

            {/* Patient Info Sidebar - Visible when job is not completed */}
            {isStatusViewActive && selectedAppointment && (
              <div className="w-full 2xl:w-1/5 flex-shrink-0">
                <PatientInfoSidebar
                  selectedAppointment={selectedAppointment}
                  onBack={() => {
                    console.log('🔙 Back button pressed - starting cleanup...');

                    // FIRST: Force stop any active recording and release microphone
                    forceCleanup();

                    // Clear status view
                    setIsStatusViewActive(false);

                    // Cleanup section recording subscription
                    console.log(
                      '🧹 Cleaning up section recording subscription'
                    );
                    cleanupSubscription();

                    // Clear Redux store
                    dispatch(clearSelectedAppointment());
                    dispatch(resetRecording());

                    // Clear all local state
                    currentAppointmentRef.current = null;
                    setAppointmentData({
                      jobDetails: null,
                      transcriptContent: null,
                      processedTranscript: null,
                      isLoading: false,
                      error: null,
                      lastFetchedAppointmentId: null,
                    });
                    setLiveProcessedTranscript(initialProcessedTranscript);
                    setIsTranscriptVisible(false);
                    setHighlightedSegmentIds([]);
                    setSelectedSection('');
                    setCurrentAudioElement(null);
                    clearTranscript();

                    console.log(
                      '✅ Cleanup complete - ready for new appointment'
                    );
                  }}
                />
              </div>
            )}

            {/* Main AI Scribe Content */}
            {selectedAppointment ? (
              <div className="flex-1 flex flex-col relative w-full 2xl:w-auto">
                {/* Show centered loader during appointment processing */}
                {aiScribeLoading ? (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                      <div className="relative w-16 h-16 mx-auto mb-6">
                        <div className="w-16 h-16 border-4 border-eyevantage-primary/20 border-t-eyevantage-primary rounded-full animate-spin" />
                        <div className="absolute inset-2 border-2 border-eyevantage-secondary/20 border-t-eyevantage-secondary rounded-full animate-spin animate-reverse" />
                      </div>
                      <h3 className="text-2xl font-light text-slate-700 dark:text-slate-300 mb-5">
                        {selectedAppointment?.scribeJobId
                          ? 'Fetching Encounter Details'
                          : 'Checking Job Status'}
                      </h3>
                      <p className="text-md text-slate-500 dark:text-slate-400 mb-4">
                        {selectedAppointment?.scribeJobId
                          ? 'Looking up recording and transcript for this encounter...'
                          : 'Looking up existing recording and transcript for this appointment...'}
                      </p>
                      <div className="w-24 h-1 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full mx-auto opacity-60" />
                    </div>
                  </div>
                ) : aiScribeError ? (
                  /* Show error state */
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                      <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">
                        Appointment Error
                      </h3>
                      <p className="text-sm text-red-600 dark:text-red-400 max-w-md">
                        {aiScribeError}
                      </p>
                    </div>
                  </div>
                ) : appointmentData.jobDetails ? (
                  /* Show playback sections when job exists */
                  <>
                    {/* Mobile: Patient Info Bar above audio controls (hidden in status view) */}
                    {!isStatusViewActive && (
                      <PatientInfoBar
                        selectedAppointment={selectedAppointment}
                        className="mb-4 md:hidden"
                      />
                    )}

                    {/* Mobile: Audio Controls below PatientInfoBar */}
                    <div
                      className={`mb-4 ${
                        isStatusViewActive ? '' : 'md:hidden'
                      }`}>
                      <AiScribeAudioControls
                        selectedAppointment={selectedAppointment}
                        jobDetails={appointmentData.jobDetails}
                        aiScribeLoading={aiScribeLoading}
                        selectedLocationId={selectedLocationId}
                        isTranscriptVisible={isTranscriptVisible}
                        isLiveTranscriptVisible={
                          liveProcessedTranscript.clickableLines.length > 0
                        }
                        onToggleTranscript={handleToggleTranscriptVisibility}
                        onAudioPlayStateChange={setIsAudioPlaying}
                        externalIsPlaying={isAudioPlaying}
                        onRecordingStopped={handleRecordingStopped}
                        onAudioElementChange={setCurrentAudioElement}
                        isVisible={
                          isStatusViewActive || currentBreakpoint === 'mobile'
                        }
                      />
                    </div>

                    {/* iPad: Side-by-side layout (hidden in status view) */}
                    {!isStatusViewActive && (
                      <div className="mb-4 hidden md:flex md:gap-4 2xl:hidden">
                        {/* Patient Info Bar - Left 50% */}
                        <div className="w-1/2">
                          <PatientInfoBar
                            selectedAppointment={selectedAppointment}
                            className=""
                          />
                        </div>

                        {/* Audio Controls - Right 50% */}
                        <div className="w-1/2">
                          <AiScribeAudioControls
                            selectedAppointment={selectedAppointment}
                            jobDetails={appointmentData.jobDetails}
                            aiScribeLoading={aiScribeLoading}
                            selectedLocationId={selectedLocationId}
                            isTranscriptVisible={isTranscriptVisible}
                            isLiveTranscriptVisible={
                              liveProcessedTranscript.clickableLines.length > 0
                            }
                            onToggleTranscript={
                              handleToggleTranscriptVisibility
                            }
                            onAudioPlayStateChange={setIsAudioPlaying}
                            externalIsPlaying={isAudioPlaying}
                            onRecordingStopped={handleRecordingStopped}
                            onAudioElementChange={setCurrentAudioElement}
                            isVisible={currentBreakpoint === 'tablet'}
                          />
                        </div>
                      </div>
                    )}

                    {/* Desktop: Audio Controls only (PatientInfoBar hidden because AppointmentList sidebar is visible or in status view) */}
                    {!isStatusViewActive && (
                      <div className="mb-4 hidden 2xl:block">
                        <AiScribeAudioControls
                          selectedAppointment={selectedAppointment}
                          jobDetails={appointmentData.jobDetails}
                          aiScribeLoading={aiScribeLoading}
                          selectedLocationId={selectedLocationId}
                          isTranscriptVisible={isTranscriptVisible}
                          isLiveTranscriptVisible={
                            liveProcessedTranscript.clickableLines.length > 0
                          }
                          onToggleTranscript={handleToggleTranscriptVisibility}
                          onAudioPlayStateChange={setIsAudioPlaying}
                          externalIsPlaying={isAudioPlaying}
                          onRecordingStopped={handleRecordingStopped}
                          onAudioElementChange={setCurrentAudioElement}
                          isVisible={currentBreakpoint === 'desktop'}
                        />
                      </div>
                    )}

                    {/* Content Panels Container */}
                    <motion.div
                      className="flex-1 flex relative"
                      layout
                      transition={{
                        type: 'spring',
                        stiffness: 200,
                        damping: 30,
                        duration: 0.2,
                      }}>
                      {/* Enhanced glass separator */}
                      {isTranscriptVisible && (
                        <motion.div
                          className={`absolute inset-y-0 ${
                            isStatusViewActive ? 'left-[35%]' : 'left-[35%]'
                          } w-px bg-gradient-to-b from-transparent via-white/15 to-transparent dark:via-white/8 z-20 hidden md:block`}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.2 }}
                        />
                      )}

                      {/* Transcript Component with existing data */}
                      <motion.div
                        layout
                        className={`flex-shrink-0 ${
                          isTranscriptVisible
                            ? isStatusViewActive
                              ? 'w-full md:w-[35%] md:pr-2'
                              : 'w-full md:w-[35%] md:pr-2'
                            : 'w-0'
                        }`}
                        initial={false}
                        animate={{
                          opacity: isTranscriptVisible ? 1 : 0,
                        }}
                        transition={
                          shouldAnimateTranscript
                            ? {
                                type: 'spring',
                                stiffness: 200,
                                damping: 30,
                                duration: 0.2,
                              }
                            : { duration: 0 }
                        }>
                        <AiScribeTranscript
                          selectedAppointment={selectedAppointment}
                          jobDetails={appointmentData.jobDetails}
                          aiScribeLoading={aiScribeLoading}
                          aiScribeError={aiScribeError}
                          aiScribeJobCompleted={aiScribeJobCompleted}
                          processingStatus={processingStatus}
                          estimatedCompletionTime={estimatedCompletionTime}
                          isVisible={isTranscriptVisible}
                          onToggleVisibility={handleToggleTranscriptVisibility}
                          isLiveTranscriptTyping={isLiveTranscriptTyping}
                          isLiveTranscriptConnected={isLiveTranscriptConnected}
                          liveTranscriptContent={liveTranscriptFromHook}
                          liveTranscriptError={liveTranscriptError}
                          scribeJobId={realtimeJobId}
                          processedTranscript={
                            appointmentData.processedTranscript ||
                            liveProcessedTranscript
                          }
                          isTranscriptLoading={isTranscriptLoading}
                          existingTranscriptError={aiScribeError}
                          onTranscriptLineClick={(startTime) => {
                            if (
                              currentAudioElement &&
                              currentAudioElement.duration
                            ) {
                              currentAudioElement.currentTime = startTime;
                              currentAudioElement
                                .play()
                                .then(() => {
                                  setIsAudioPlaying(true);
                                })
                                .catch(console.error);
                            } else {
                              console.warn(
                                '⚠️ No audio element found or not ready'
                              );
                            }
                          }}
                          highlightedSegmentIds={highlightedSegmentIds}
                          onHighlightSegments={handleHighlightSegments}
                        />
                      </motion.div>

                      {/* Insights Component with existing data */}
                      <motion.div
                        layout
                        className={`${
                          isTranscriptVisible ? 'hidden md:flex' : 'flex'
                        } flex-1 md:pl-2`}
                        initial={false}
                        animate={{
                          opacity: 1,
                        }}
                        transition={{
                          type: 'spring',
                          stiffness: 200,
                          damping: 30,
                          duration: 0.2,
                        }}>
                        <AiScribeInsights
                          selectedAppointment={selectedAppointment}
                          jobDetails={appointmentData.jobDetails}
                          aiScribeLoading={aiScribeLoading}
                          isTranscriptVisible={isTranscriptVisible}
                          scribeJobId={realtimeJobId}
                          realtimeEnabled={processingStarted}
                          selectedSection={selectedSection}
                          onHighlightSegments={handleHighlightSegments}
                          onSelectedSection={setSelectedSection}
                        />
                      </motion.div>
                    </motion.div>
                  </>
                ) : (
                  /* Show recording sections when no job exists */
                  <>
                    {/* Mobile: Patient Info Bar above audio controls (hidden in status view) */}
                    {!isStatusViewActive && (
                      <PatientInfoBar
                        selectedAppointment={selectedAppointment}
                        className="mb-4 md:hidden"
                      />
                    )}

                    {/* Mobile: Audio Controls below PatientInfoBar */}
                    <div
                      className={`mb-4 ${
                        isStatusViewActive ? '' : 'md:hidden'
                      }`}>
                      <AiScribeAudioControls
                        selectedAppointment={selectedAppointment}
                        jobDetails={appointmentData.jobDetails}
                        isLiveTranscriptVisible={
                          liveProcessedTranscript.clickableLines.length > 0
                        }
                        aiScribeLoading={aiScribeLoading}
                        selectedLocationId={selectedLocationId}
                        isTranscriptVisible={isTranscriptVisible}
                        onToggleTranscript={handleToggleTranscriptVisibility}
                        onAudioPlayStateChange={setIsAudioPlaying}
                        externalIsPlaying={isAudioPlaying}
                        onRecordingStopped={handleRecordingStopped}
                        onAudioElementChange={setCurrentAudioElement}
                        isVisible={
                          isStatusViewActive || currentBreakpoint === 'mobile'
                        }
                      />
                    </div>

                    {/* iPad: Side-by-side layout (hidden in status view) */}
                    {!isStatusViewActive && (
                      <div className="mb-4 hidden md:flex md:gap-4 2xl:hidden">
                        {/* Patient Info Bar - Left 50% */}
                        <div className="w-1/2">
                          <PatientInfoBar
                            selectedAppointment={selectedAppointment}
                            className=""
                          />
                        </div>

                        {/* Audio Controls - Right 50% */}
                        <div className="w-1/2">
                          <AiScribeAudioControls
                            selectedAppointment={selectedAppointment}
                            jobDetails={appointmentData.jobDetails}
                            isLiveTranscriptVisible={
                              liveProcessedTranscript.clickableLines.length > 0
                            }
                            aiScribeLoading={aiScribeLoading}
                            selectedLocationId={selectedLocationId}
                            isTranscriptVisible={isTranscriptVisible}
                            onToggleTranscript={
                              handleToggleTranscriptVisibility
                            }
                            onAudioPlayStateChange={setIsAudioPlaying}
                            externalIsPlaying={isAudioPlaying}
                            onRecordingStopped={handleRecordingStopped}
                            onAudioElementChange={setCurrentAudioElement}
                            isVisible={currentBreakpoint === 'tablet'}
                          />
                        </div>
                      </div>
                    )}

                    {/* Desktop: Audio Controls only (PatientInfoBar hidden because AppointmentList sidebar is visible or in status view) */}
                    {!isStatusViewActive && (
                      <div className="mb-4 hidden 2xl:block">
                        <AiScribeAudioControls
                          selectedAppointment={selectedAppointment}
                          jobDetails={appointmentData.jobDetails}
                          isLiveTranscriptVisible={
                            liveProcessedTranscript.clickableLines.length > 0
                          }
                          aiScribeLoading={aiScribeLoading}
                          selectedLocationId={selectedLocationId}
                          isTranscriptVisible={isTranscriptVisible}
                          onToggleTranscript={handleToggleTranscriptVisibility}
                          onAudioPlayStateChange={setIsAudioPlaying}
                          externalIsPlaying={isAudioPlaying}
                          onRecordingStopped={handleRecordingStopped}
                          onAudioElementChange={setCurrentAudioElement}
                          isVisible={currentBreakpoint === 'desktop'}
                        />
                      </div>
                    )}

                    {/* Content Panels Container */}
                    <motion.div
                      className="flex-1 flex relative"
                      layout
                      transition={{
                        type: 'spring',
                        stiffness: 200,
                        damping: 30,
                        duration: 0.2,
                      }}>
                      {/* Enhanced glass separator */}
                      {isTranscriptVisible && (
                        <motion.div
                          className={`absolute inset-y-0 ${
                            isStatusViewActive ? 'left-[35%]' : 'left-[35%]'
                          } w-px bg-gradient-to-b from-transparent via-white/15 to-transparent dark:via-white/8 z-20 hidden md:block`}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.2 }}
                        />
                      )}

                      {/* Transcript Component for new recording */}
                      <motion.div
                        layout
                        className={`flex-shrink-0 ${
                          isTranscriptVisible
                            ? isStatusViewActive
                              ? 'w-full md:w-[35%] md:pr-2'
                              : 'w-full md:w-[35%] md:pr-2'
                            : 'w-0'
                        }`}
                        initial={false}
                        animate={{
                          opacity: isTranscriptVisible ? 1 : 0,
                        }}
                        transition={
                          shouldAnimateTranscript
                            ? {
                                type: 'spring',
                                stiffness: 200,
                                damping: 30,
                                duration: 0.2,
                              }
                            : { duration: 0 }
                        }>
                        <AiScribeTranscript
                          selectedAppointment={selectedAppointment}
                          jobDetails={appointmentData.jobDetails}
                          aiScribeLoading={aiScribeLoading}
                          aiScribeError={aiScribeError}
                          aiScribeJobCompleted={aiScribeJobCompleted}
                          processingStatus={processingStatus}
                          estimatedCompletionTime={estimatedCompletionTime}
                          isVisible={isTranscriptVisible}
                          onToggleVisibility={handleToggleTranscriptVisibility}
                          scribeJobId={realtimeJobId}
                          liveTranscriptContent={liveTranscriptFromHook}
                          isLiveTranscriptTyping={isLiveTranscriptTyping}
                          isLiveTranscriptConnected={isLiveTranscriptConnected}
                          liveTranscriptError={liveTranscriptError}
                          processedTranscript={
                            appointmentData.processedTranscript ||
                            liveProcessedTranscript
                          }
                          isTranscriptLoading={isTranscriptLoading}
                          existingTranscriptError={aiScribeError}
                          onTranscriptLineClick={(startTime) => {
                            console.log(
                              '🎯 Transcript line clicked, seeking to:',
                              startTime,
                              'seconds'
                            );
                            if (
                              currentAudioElement &&
                              currentAudioElement.duration
                            ) {
                              console.log(
                                '🎵 Audio found, current time:',
                                currentAudioElement.currentTime,
                                'duration:',
                                currentAudioElement.duration
                              );
                              currentAudioElement.currentTime = startTime;
                              console.log('🎵 Audio time set to:', startTime);
                              currentAudioElement
                                .play()
                                .then(() => {
                                  setIsAudioPlaying(true);
                                  console.log(
                                    '🎵 Audio started playing, updated state'
                                  );
                                })
                                .catch(console.error);
                            } else {
                              console.warn(
                                '⚠️ No audio element found or not ready'
                              );
                            }
                          }}
                          highlightedSegmentIds={highlightedSegmentIds}
                          onHighlightSegments={handleHighlightSegments}
                        />
                      </motion.div>

                      {/* Insights Component for new recording */}
                      <motion.div
                        layout
                        className={`${
                          isTranscriptVisible ? 'hidden md:flex' : 'flex'
                        } flex-1 md:pl-2`}
                        initial={false}
                        animate={{
                          opacity: 1,
                        }}
                        transition={{
                          type: 'spring',
                          stiffness: 200,
                          damping: 30,
                          duration: 0.2,
                        }}>
                        <AiScribeInsights
                          selectedAppointment={selectedAppointment}
                          jobDetails={appointmentData.jobDetails}
                          aiScribeLoading={aiScribeLoading}
                          isTranscriptVisible={isTranscriptVisible}
                          scribeJobId={realtimeJobId}
                          selectedSection={selectedSection}
                          realtimeEnabled={processingStarted}
                          onHighlightSegments={handleHighlightSegments}
                          onSelectedSection={setSelectedSection}
                        />
                      </motion.div>
                    </motion.div>
                  </>
                )}
              </div>
            ) : (
              <div className="flex justify-center flex-1 h-full w-full 2xl:w-auto">
                <GlassCard
                  variant="subtle"
                  className="h-full w-full 2xl:w-auto">
                  <div
                    className="text-center"
                    style={{ height: 'calc(100vh - 11rem)' }}>
                    {/* Enhanced welcome icon - no movement */}
                    <div className="flex flex-col items-center justify-center h-full">
                      <div className="relative w-40 h-40 mx-auto mb-6">
                        <div className="relative w-full h-full bg-gradient-to-br from-purple-500/15 via-purple-500/8 to-blue-500/15 rounded-3xl flex items-center justify-center border border-white/20 dark:border-white/10 backdrop-blur-sm">
                          {/* Inner layers */}
                          <div className="absolute inset-[1px] rounded-3xl bg-gradient-to-br from-white/30 via-white/15 to-white/5 dark:from-white/15 dark:via-white/8 dark:to-transparent" />
                          <div className="absolute inset-[1px] rounded-3xl bg-gradient-to-t from-transparent via-transparent to-white/20 dark:to-white/10" />

                          <div className="relative flex items-center justify-center">
                            <Mic className="w-20 h-20 text-purple-500" />
                            <Sparkles className="w-10 h-10 text-blue-500 absolute -top-5 -right-5 animate-pulse" />
                          </div>
                        </div>
                      </div>

                      {/* Enhanced typography */}

                      <h3 className="text-4xl font-light text-slate-600 dark:text-slate-200 mb-6 tracking-wide">
                        Welcome to Ai Scribe
                      </h3>
                      <div className="w-60 h-0.5 bg-gradient-to-r from-transparent via-purple-500 to-transparent mx-auto mb-6 animate-pulse" />
                      <div className="flex flex-col items-center justify-center text-slate-600 dark:text-slate-400 text-md leading-relaxed max-w-xs mx-auto gap-5">
                        <p>Select an appointment to get started</p>
                        <p>Or</p>

                        <Button
                          size="lg"
                          onClick={() => setIsNewEncounterDialogOpen(true)}
                          className="bg-gradient-to-r from-purple-500 to-blue-800 text-md text-white rounded-3xl">
                          <Plus className="w-4 h-4 mr-1 text-white" />
                          Create Ai Encounter
                        </Button>
                      </div>
                    </div>
                  </div>
                </GlassCard>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* New Ai Encounter Dialog */}
      <NewEncounterDialog
        open={isNewEncounterDialogOpen}
        onOpenChange={setIsNewEncounterDialogOpen}
        onSubmit={handleCreateNewEncounter}
        isSubmitting={isCreatingEncounter}
      />
    </Layout>
  );
};

// Wrapper component with RecordingProvider
const AiScribe = () => {
  return (
    <RecordingProvider>
      <AiScribeContent />
    </RecordingProvider>
  );
};

export default AiScribe;
